/**
 * AVTR Quick Add — Luxury product card AJAX cart system
 * Represent-style micro-interactions (vanilla JS, no Alpine.js)
 *
 * Responsibilities:
 *  1. Desktop: mouseenter ATC icon → overlay visible; mouseleave card → close
 *  2. Mobile: tap ATC → open overlay; tap size → add to cart immediately
 *  3. POST to /cart/add.js (items array format) on size selection
 *  4. Open Dawn's CartDrawer after successful add
 *  5. Visual feedback: loading spinner → success ✓ → reset
 *  6. Error feedback: "Sin stock" label on 422/sold-out
 *  7. Re-init on facets update (MutationObserver on #product-grid)
 */

(function () {
  'use strict';

  /* ── Helpers ─────────────────────────────────────────────── */

  function openCartDrawer() {
    console.log('[AVTR Quick Add] Trying to open cart drawer...');
    
    let opened = false;
    if (window.Alpine) {
      try {
        const bodyData = window.Alpine.$data(document.body);
        if (bodyData) {
          console.log('[AVTR Quick Add] Direct Alpine access succeeded. Setting isCartOpen = true and calling refreshCart()...');
          bodyData.isCartOpen = true;
          bodyData.refreshCart();
          opened = true;
        }
      } catch (err) {
        console.warn('[AVTR Quick Add] Direct Alpine access failed:', err);
      }
    }
    
    if (!opened) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        console.log('[AVTR Quick Add] Calling drawer.open() on <cart-drawer> custom element...');
        drawer.open();
      } else {
        console.log('[AVTR Quick Add] Fallback: Dispatching cart:refresh and cart:open events on document...');
        document.dispatchEvent(new CustomEvent('cart:refresh'));
        document.dispatchEvent(new CustomEvent('cart:open'));
      }
    }
  }

  /**
   * Show error message on a card, auto-hide after 2.5s
   */
  function showError(card, message) {
    const errEl = card.querySelector('.avtr-card__error');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.classList.add('is-visible');
    setTimeout(() => errEl.classList.remove('is-visible'), 2500);
  }

  /**
   * Set ATC button state: 'idle' | 'loading' | 'success'
   */
  function setAtcState(btn, state) {
    btn.classList.remove('is-loading', 'is-success');
    btn.disabled = false;
    if (state === 'loading') {
      btn.classList.add('is-loading');
      btn.disabled = true;
    } else if (state === 'success') {
      btn.classList.add('is-success');
      setTimeout(() => {
        btn.classList.remove('is-success');
      }, 1200);
    }
  }

  /**
   * POST variant to Shopify cart using items[] array format
   */
  async function addToCart(variantId, card, btn) {
    console.log(`[AVTR Quick Add] Initiating addToCart for variant: ${variantId}`);
    setAtcState(btn, 'loading');

    // Get pack value from card dataset
    const packValue = card.dataset.packValue;

    // Build payload
    const item = {
      id: Number(variantId),
      quantity: 1
    };
    if (packValue) {
      item.properties = {
        bundlePack: packValue
      };
    }

    try {
      const addUrl = (window.routes && window.routes.cart_add_url) ? window.routes.cart_add_url : '/cart/add.js';
      console.log(`[AVTR Quick Add] Sending POST request to: ${addUrl}`, item);
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json', 
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ items: [item] }),
      });

      console.log(`[AVTR Quick Add] Response received status: ${response.status}`);
      if (response.ok) {
        const responseJson = await response.clone().json().catch(() => ({}));
        console.log('[AVTR Quick Add] Success response JSON:', responseJson);
        setAtcState(btn, 'success');
        setTimeout(() => {
          card.classList.remove('is-adding');
        }, 1200);
        openCartDrawer();
      } else {
        const data = await response.json().catch(() => ({}));
        console.warn('[AVTR Quick Add] Add to cart failed response data:', data);
        setAtcState(btn, 'idle');
        card.classList.remove('is-adding');
        showError(card, data.description || 'Sin stock');
      }
    } catch (err) {
      console.error('[AVTR Quick Add] Network or execution error adding to cart:', err);
      setAtcState(btn, 'idle');
      card.classList.remove('is-adding');
      showError(card, 'Error de red');
    }
  }

  /* ── Card initializer ────────────────────────────────────── */

  function initCard(card) {
    if (card.dataset.avtrInit) return;
    card.dataset.avtrInit = '1';

    const atcBtn      = card.querySelector('.avtr-card__atc-btn');
    const sizeOverlay = card.querySelector('.avtr-card__size-overlay');
    const sizeBtns    = card.querySelectorAll('.avtr-card__size-btn');
    const hasVariants = sizeBtns.length > 0;

    let selectedVariantId = null;

    /* ── Overlay helpers ── */
    function openOverlay() { card.classList.add('avtr-sizes-open'); }
    function closeOverlay() { card.classList.remove('avtr-sizes-open'); }

    /* ── Size button clicks ── */
    sizeBtns.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();

        sizeBtns.forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');
        selectedVariantId = btn.dataset.variantId;

        // Optimistic UI: Immediately close size selector panel and freeze it using is-adding
        closeOverlay();
        card.classList.add('is-adding');

        // On any device: clicking a size always adds to cart immediately
        if (atcBtn) addToCart(selectedVariantId, card, atcBtn);
      });
    });

    /* ── Hover triggers (Exclusive to "+" button on desktop) ── */
    if (atcBtn && hasVariants) {
      atcBtn.addEventListener('mouseenter', () => {
        if (window.matchMedia('(hover: hover)').matches) {
          openOverlay();
        }
      });
      
      // Mobile tap on "+" opens overlay
      atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        openOverlay();
      });
    } else if (atcBtn) {
      // Single-variant product: clicking "+" adds to cart directly
      atcBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const variantId = card.dataset.defaultVariant;
        if (variantId) {
          card.classList.add('is-adding');
          addToCart(variantId, card, atcBtn);
        }
      });
    }

    /* ── Hover maintenance on the overlay ── */
    if (sizeOverlay) {
      sizeOverlay.addEventListener('mouseleave', () => {
        closeOverlay();
      });
    }

    /* ── Close overlay on card mouseleave (desktop fallback) ── */
    card.addEventListener('mouseleave', () => {
      closeOverlay();
      card.classList.remove('is-adding');
    });
  }

  /* ── Global delegated close on outside click ─────────────── */
  // ONE global handler (not per-card) to avoid listener accumulation

  document.addEventListener('click', (e) => {
    document.querySelectorAll('.avtr-card.avtr-sizes-open').forEach((card) => {
      if (!card.contains(e.target)) card.classList.remove('avtr-sizes-open');
    });
  });

  /* ── Init all cards on page ──────────────────────────────── */

  function initAllCards() {
    document.querySelectorAll('.avtr-card[data-product-id]').forEach(initCard);
  }

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllCards);
  } else {
    initAllCards();
  }

  /* Re-init on dynamic section loads (Shopify theme editor) */
  document.addEventListener('shopify:section:load', initAllCards);

  /* ── MutationObserver: re-init after facets/pagination rerender ── */
  // facets.js replaces #product-grid innerHTML via renderProductGridContainer()
  // We watch for childList mutations and re-init new cards.

  function watchProductGrid() {
    const grid = document.querySelector('#product-grid');
    if (!grid) return;

    const observer = new MutationObserver(() => {
      initAllCards();
    });

    observer.observe(grid, { childList: true, subtree: false });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchProductGrid);
  } else {
    watchProductGrid();
  }

  /* Also watch for new grids injected after section loads */
  document.addEventListener('shopify:section:load', watchProductGrid);

})();
